import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { 
  ArrowRight, 
  Shield, 
  TrendingUp, 
  Users, 
  Award, 
  Target, 
  CheckCircle,
  Compass,
  ClipboardCheck,
  Trophy,
  Eye,
  Play
} from "lucide-react";

const EnhancedCertificationSection = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Scroll detection to stop video when scrolling away from section
  useEffect(() => {
    const handleScroll = () => {
      const section = document.querySelector('.our-story-section');
      if (section) {
        const rect = section.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        
        // Stop video when scrolling away from section
        if (!isVisible && isVideoPlaying) {
          setIsVideoPlaying(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVideoPlaying]);
  const whyItems = [
    {
      icon: Shield,
      title: "Build Credible Trust",
      description: "In today's market, stakeholders demand proof of authentic commitment to conscious business practices. CNESS certification provides the credible validation that sets you apart from greenwashing competitors.",
      image: "https://images.unsplash.com/photo-1646296066880-c61cac79470b?auto=format&fit=crop&w=800&q=80"
    },
    {
      icon: TrendingUp,
      title: "Drive Sustainable Growth", 
      description: "Conscious businesses outperform traditional companies by 3x in revenue growth. Our certification framework helps you unlock sustainable competitive advantages while staying true to your values.",
      image: "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?auto=format&fit=crop&w=800&q=80"
    },
    {
      icon: Users,
      title: "Attract Top Talent",
      description: "85% of millennials prioritize working for purpose-driven organizations. CNESS certification signals your commitment to meaningful impact, helping you attract and retain exceptional team members.",
      image: "https://images.unsplash.com/photo-1758873268631-fa944fc5cad2?auto=format&fit=crop&w=800&q=80"
    }
  ];

  const whatItems = [
    {
      icon: Award,
      title: "Comprehensive 8-Pillar Framework", 
      description: "Our holistic assessment covers Conscious Leadership, Human-Centric Culture & Team Cohesion, Products & Services, Customers & Clients, Community & Conscious Social Impact, and Brand Vision & Collaboration.",
      image: "https://images.unsplash.com/photo-1574966390692-5140d4310743"
    },
    {
      icon: Target,
      title: "Measurable Impact Metrics",
      description: "Get clear, actionable insights with our CIS (Conscious Impact Score) system. Track your progress across all pillars with data-driven recommendations for continuous improvement.", 
      image: "https://images.unsplash.com/photo-1683770997177-0603bd44d070"
    },
    {
      icon: CheckCircle,
      title: "Recognized Industry Standard",
      description: "Join a growing community of certified conscious businesses. Our certification is recognized by investors, partners, and customers as the gold standard for authentic business transformation.",
      image: "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg"
    }
  ];

  const howItems = [
    {
      icon: Compass,
      number: "01",
      title: "Discover Your Current State",
      description: "Complete our comprehensive assessment across the 6 pillars of conscious business. Get detailed insights into your current practices and identify areas for improvement.",
      cta: "Start Assessment"
    },
    {
      icon: ClipboardCheck,
      number: "02",
      title: "Implement Guided Improvements",
      description: "Work with our certified mentors and use our best practices library to enhance your operations. Access personalized learning paths and implementation resources.",
      cta: "View Resources"
    },
    {
      icon: Trophy,
      number: "03",
      title: "Achieve Certification Excellence",
      description: "Receive your official CNESS certification and join our directory of conscious businesses. Showcase your commitment and connect with like-minded organizations.",
      cta: "Get Certified"
    }
  ];

  return (
    <section className="relative py-16 overflow-hidden">
      {/* Dynamic Refreshing Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-50/50 via-transparent to-amber-50/30"></div>
        
        {/* Refreshing animated elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-indigo-300/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-emerald-200/15 to-teal-300/15 rounded-full blur-3xl animate-bounce"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-amber-100/10 to-orange-200/10 rounded-full blur-3xl"></div>
        
        {/* Dynamic pattern overlay */}
        <div className="absolute inset-0 opacity-30" 
             style={{
               backgroundImage: `
                 radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.1), transparent 50%),
                 radial-gradient(circle at 80% 70%, rgba(16, 185, 129, 0.1), transparent 50%),
                 radial-gradient(circle at 50% 50%, rgba(245, 158, 11, 0.08), transparent 50%)
               `
             }}>
        </div>
      </div>

      <div className="container mx-auto px-6 relative z-10">

        {/* Our Story Video Section */}
        <div className="mb-24 our-story-section" id="video-section">
          <div className="text-center mb-12">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-black text-gray-900 mb-4">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Story</span>
            </h3>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Discover the journey that led to creating the world's most trusted conscious business certification
            </p>
          </div>
          
          <div className="max-w-md mx-auto">
            <div className="relative bg-gradient-to-r from-purple-900 to-blue-900 rounded-2xl p-6">
              <div 
                className="relative aspect-[9/16] rounded-xl overflow-hidden max-h-[400px] mx-auto cursor-pointer group"
                onClick={() => setIsVideoPlaying(!isVideoPlaying)}
              >
                {isVideoPlaying ? (
                  <video 
                    className="w-full h-full object-cover"
                    controls
                    autoPlay
                    preload="metadata"
                    playsInline
                    style={{ minWidth: '100%', minHeight: '100%' }}
                    onPause={() => setIsVideoPlaying(false)}
                    onEnded={() => setIsVideoPlaying(false)}
                    onError={() => setIsVideoPlaying(false)}
                  >
                    <source src="/WhatsApp Video 2025-10-09 at 2.21.32 PM.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <>
                    <img 
                      src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&w=800&q=80"
                      alt="From Loneliness to Wholeness - Building Conscious Future"
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition-all">
                      <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 group-hover:scale-110 transition-all duration-300">
                        <Play className="w-8 h-8 text-gray-900 ml-1" />
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <div className="text-center mt-6">
                <h4 className="text-white font-semibold text-lg mb-2">
                  The CNESS Story: Building a Conscious Future
                </h4>
                <p className="text-white/80 text-sm">
                  How we're transforming business through consciousness and authentic impact
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* WHY Section - Welcome.com Style */}
        <div className="mb-24">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 mb-4 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
              Why Choose CNESS
            </div>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-black text-gray-900 mb-4 leading-tight">
              Why Leaders Choose 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-500"> CNESS</span>
            </h3>
            <p className="text-base text-gray-600 max-w-2xl mx-auto font-medium">
              Join extraordinary businesses transforming industries with purpose-driven excellence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyItems.map((item, index) => {
              const colors = [
                'from-blue-500 to-blue-600',
                'from-emerald-500 to-emerald-600', 
                'from-amber-500 to-amber-600'
              ];
              
              return (
                <Card 
                  key={index}
                  className="group hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer border-0 overflow-hidden bg-white"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    {/* Removed color overlay as per feedback */}
                    
                    <div className="absolute top-4 left-4">
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <item.icon className="h-5 w-5 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-gray-900 mb-3">
                      {item.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* WHAT Section - Welcome.com Style */}
        <div className="mb-24">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 mb-4 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
              What You Get
            </div>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-black text-gray-900 mb-4 leading-tight">
              Unlock Your 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600"> 
                POTENTIAL
              </span>
            </h3>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Comprehensive certification crafted for transformation and measurable impact
            </p>
          </div>
          
          <div className="relative z-10">
            {/* Flowing Excellence Cards - No Blog-like Structure */}
            <div className="max-w-7xl mx-auto space-y-16">
              {whatItems.map((item, index) => {
                const isReverse = index % 2 === 1;
                const colors = [
                  'from-purple-500 to-indigo-600',
                  'from-pink-500 to-rose-600',
                  'from-orange-500 to-amber-600'
                ];
                
                return (
                  <div 
                    key={index}
                    className={`flex flex-col ${isReverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 lg:gap-20`}
                  >
                    {/* Dynamic Image Section */}
                    <div className="flex-1 relative">
                      <div className="relative overflow-hidden rounded-3xl shadow-3xl transform hover:scale-105 transition-all duration-700">
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="w-full h-80 object-cover"
                        />
                        <div className={`absolute inset-0 bg-gradient-to-br ${colors[index]} opacity-40 mix-blend-overlay`}></div>
                        
                        {/* Floating excellence badge */}
                        <div className="absolute top-6 right-6">
                          <div className={`w-20 h-20 rounded-full bg-gradient-to-r ${colors[index]} flex items-center justify-center shadow-2xl animate-bounce`}>
                            <item.icon className="h-10 w-10 text-white" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Floating elements */}
                      <div className={`absolute -bottom-4 ${isReverse ? '-right-4' : '-left-4'} w-24 h-24 bg-gradient-to-r ${colors[index]} rounded-full blur-xl opacity-50 animate-pulse`}></div>
                    </div>
                    
                    {/* Excellence Content */}
                    <div className="flex-1 text-center lg:text-left">
                      <div className="inline-flex items-center px-6 py-3 mb-6 bg-white/80 backdrop-blur-sm rounded-full shadow-xl">
                        <span className="text-3xl mr-3">⚡</span>
                        <span className={`font-black text-lg bg-gradient-to-r ${colors[index]} bg-clip-text text-transparent`}>
                          Excellence #{index + 1}
                        </span>
                      </div>
                      
                      <h4 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
                        {item.title}
                      </h4>
                      
                      <p className="text-xl text-gray-700 leading-relaxed font-medium mb-8">
                        {item.description}
                      </p>
                      
                      {/* Excellence metrics */}
                      <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                        <div className="px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full">
                          <span className="text-sm font-bold text-gray-800">🚀 Proven Results</span>
                        </div>
                        <div className="px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full">
                          <span className="text-sm font-bold text-gray-800">⭐ Industry Leading</span>
                        </div>
                        <div className="px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full">
                          <span className="text-sm font-bold text-gray-800">💎 Premium Quality</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* HOW Section - WOW FACTOR TRANSFORMATION */}
        <div className="mb-40 relative overflow-hidden">
          {/* WOW Background Elements */}
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-20 left-1/4 w-96 h-96 bg-gradient-to-br from-cyan-300/20 to-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-gradient-to-br from-violet-300/15 to-purple-500/15 rounded-full blur-3xl animate-bounce"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-emerald-200/10 to-teal-400/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="text-center mb-24 relative z-10">
            {/* WOW FACTOR Badge */}
            <div className="inline-flex items-center px-12 py-6 mb-12 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 text-white rounded-full text-xl font-black shadow-2xl transform hover:scale-110 transition-all duration-500">
              <span className="text-3xl mr-4">🚀</span>
              PROVEN METHODOLOGY
            </div>
            
            {/* UNIFORM INTERNATIONAL Typography - Level 1 */}
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-black text-gray-900 mb-8 leading-tight tracking-tight">
              How Champions
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600"> 
                TRANSFORM
              </span>
            </h3>
            
            {/* INTERNATIONAL STANDARD Typography - Level 2 */}
            <p className="text-lg md:text-xl text-gray-700 max-w-5xl mx-auto font-bold leading-relaxed">
              🌟 Our revolutionary three-step methodology that transforms visionaries into certified champions of conscious business excellence!
            </p>
            
            {/* WOW FACTOR Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-16">
              <div className="text-center">
                <div className="text-5xl font-black text-cyan-600">97%</div>
                <div className="text-lg font-bold text-gray-700">Success Rate</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black text-blue-600">30</div>
                <div className="text-lg font-bold text-gray-700">Days Avg</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black text-purple-600">500+</div>
                <div className="text-lg font-bold text-gray-700">Champions</div>
              </div>
            </div>
          </div>
          
          {/* WOW FACTOR Process Timeline */}
          <div className="relative z-10">
            {/* Champion Timeline */}
            <div className="max-w-7xl mx-auto">
              <div className="relative">
                {/* Timeline Line */}
                <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-2 bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 rounded-full transform -translate-y-1/2"></div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 relative">
                  {howItems.map((item, index) => {
                    const wowColors = [
                      { 
                        primary: 'from-cyan-500 to-blue-600',
                        secondary: 'from-cyan-50 to-blue-100',
                        accent: 'text-cyan-600',
                        glow: 'shadow-cyan-500/50'
                      },
                      { 
                        primary: 'from-blue-500 to-purple-600', 
                        secondary: 'from-blue-50 to-purple-100',
                        accent: 'text-blue-600',
                        glow: 'shadow-blue-500/50'
                      },
                      { 
                        primary: 'from-purple-500 to-violet-600',
                        secondary: 'from-purple-50 to-violet-100', 
                        accent: 'text-purple-600',
                        glow: 'shadow-purple-500/50'
                      }
                    ];
                    
                    const colors = wowColors[index];
                    const ctaLink = item.cta === "Start Assessment" ? "/signup" : 
                                  item.cta === "View Resources" ? "/support" : 
                                  item.cta === "Get Certified" ? "/signup" : "/signup";
                    
                    return (
                      <div key={index} className="relative">
                        {/* WOW FACTOR Card */}
                        <div className="group relative transform hover:scale-110 transition-all duration-700 hover:z-20">
                          {/* Floating Champion Number */}
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-30">
                            <div className={`w-20 h-20 rounded-full bg-gradient-to-r ${colors.primary} flex items-center justify-center shadow-2xl ${colors.glow} animate-bounce`}>
                              <span className="text-white font-black text-2xl">{item.number}</span>
                            </div>
                          </div>
                          
                          {/* Main Card */}
                          <div className={`relative bg-gradient-to-br ${colors.secondary} rounded-3xl p-10 shadow-2xl border-4 border-white group-hover:shadow-3xl transition-all duration-500 mt-12`}>
                            {/* WOW Background Pattern */}
                            <div className="absolute inset-0 opacity-10 rounded-3xl overflow-hidden">
                              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/50 to-transparent rounded-full blur-xl"></div>
                              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-white/30 to-transparent rounded-full blur-lg"></div>
                            </div>
                            
                            {/* Champion Icon */}
                            <div className="relative z-10 text-center mb-8">
                              <div className={`w-24 h-24 mx-auto rounded-full bg-gradient-to-r ${colors.primary} flex items-center justify-center shadow-xl group-hover:rotate-12 transition-transform duration-500`}>
                                <item.icon className="h-12 w-12 text-white" />
                              </div>
                            </div>
                            
                            {/* UNIFORM Typography - Level 2 Title */}
                            <h4 className={`text-3xl font-black text-gray-900 mb-6 text-center leading-tight ${colors.accent} group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:${colors.primary} transition-all duration-300`}>
                              {item.title}
                            </h4>
                            
                            {/* UNIFORM Typography - Description */}
                            <p className="text-lg text-gray-700 text-center leading-relaxed font-medium mb-8">
                              {item.description}
                            </p>
                            
                            {/* Champion Action */}
                            <div className="text-center">
                              <a href={ctaLink}>
                                <Button className={`bg-gradient-to-r ${colors.primary} text-white px-8 py-4 text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300`}>
                                  <Trophy className="mr-3 h-5 w-5" />
                                  {item.cta}
                                  <ArrowRight className="ml-3 h-5 w-5" />
                                </Button>
                              </a>
                            </div>
                          </div>
                          
                          {/* Floating Achievement Badges */}
                          <div className="absolute -bottom-4 -right-4 opacity-0 group-hover:opacity-100 transition-all duration-500">
                            <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${colors.primary} flex items-center justify-center shadow-xl animate-spin`}>
                              <span className="text-white text-2xl">⚡</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* WOW FACTOR Final CTA Section */}
        <div className="relative mb-32 overflow-hidden">
          {/* Dynamic Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50"></div>
          <div className="absolute inset-0 bg-gradient-to-tr from-amber-50/30 via-transparent to-emerald-50/30"></div>
          
          {/* Floating Elements */}
          <div className="absolute top-10 right-20 w-64 h-64 bg-gradient-to-br from-violet-200/20 to-purple-300/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-gradient-to-br from-indigo-200/15 to-blue-300/15 rounded-full blur-3xl animate-bounce"></div>
          
          <div className="relative z-10 text-center rounded-3xl p-16">
            {/* WOW Badge */}
            <div className="inline-flex items-center px-10 py-5 mb-10 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-full text-xl font-black shadow-2xl">
              <span className="text-3xl mr-4">🎯</span>
              READY TO LEAD?
            </div>
            
            {/* UNIFORM Typography - Level 1 */}
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-black text-gray-900 mb-8 leading-tight">
              Transform Your Business 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-purple-600 to-indigo-600"> 
                TODAY
              </span>
            </h3>
            
            {/* UNIFORM Typography - Level 2 */}
            <p className="text-lg md:text-xl text-gray-700 mb-12 max-w-4xl mx-auto font-bold leading-relaxed">
              🚀 Join the elite community of certified conscious business champions making extraordinary impact worldwide!
            </p>
            
            {/* WOW Champion Actions */}
            <div className="flex flex-col sm:flex-row gap-8 justify-center items-center mb-12">
              <a href="/signup">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-violet-500 to-purple-600 text-white px-12 py-6 text-xl font-black hover:from-violet-600 hover:to-purple-700 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 rounded-2xl"
                >
                  <Trophy className="mr-3 h-6 w-6" />
                  Start Your Champion Journey
                  <ArrowRight className="ml-3 h-6 w-6" />
                </Button>
              </a>
              
              <a href="/pillars">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-3 border-violet-400 text-violet-600 px-10 py-6 text-xl font-black hover:bg-violet-50 transition-all duration-300 rounded-2xl"
                >
                  <Eye className="mr-3 h-6 w-6" />
                  Explore 6 Pillars
                </Button>
              </a>
            </div>
            
            {/* Champion Stats */}
            <div className="flex flex-wrap justify-center gap-12 text-center">
              <div>
                <div className="text-4xl font-black text-violet-600">24/7</div>
                <div className="text-lg font-bold text-gray-700">Expert Support</div>
              </div>
              <div>
                <div className="text-4xl font-black text-purple-600">100%</div>
                <div className="text-lg font-bold text-gray-700">Satisfaction</div>
              </div>
              <div>
                <div className="text-4xl font-black text-indigo-600">∞</div>
                <div className="text-lg font-bold text-gray-700">Possibilities</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnhancedCertificationSection;